import React from 'react'
import { Link } from 'react-router-dom'
import AuthLayout, { AUTH_ACCENT, authFieldClass } from '../../../components/AuthLayout'
import PasswordStrength from '../../../components/PasswordStrength'
import { AlertIcon, EyeIcon, EyeOffIcon } from '../../../template/kartera/icons'
import { LOGIN_PATH, REGISTER_PATH } from '../common/register-common'
import { CODE_LENGTH, useRegisterActivateModel } from './register-activate-model'

const RegisterActivate = () => {
    const model = useRegisterActivateModel()

    const passwordField = (props: { id: string; value: string; invalid: boolean; autoComplete: string; onChange: (value: string) => void }) => (
        <span className={`flex h-[50px] items-center overflow-hidden rounded-xl bg-k-surface ${props.invalid ? 'border-2 border-k-danger' : 'border border-k-line-strong'}`}>
            <input
                id={props.id}
                type={model.showPassword ? 'text' : 'password'}
                autoComplete={props.autoComplete}
                value={props.value}
                aria-invalid={props.invalid}
                onChange={(event) => props.onChange(event.target.value)}
                className="h-full min-w-0 flex-1 border-0 bg-transparent px-3.5 text-[15px] text-k-ink outline-none"
            />
            <button
                type="button"
                aria-label={model.showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                aria-pressed={model.showPassword}
                onClick={model.toggleShowPassword}
                className="flex h-full w-11 shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent text-k-muted"
            >
                {model.showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
        </span>
    )

    return (
        <AuthLayout helpText="¿No te llegó el correo?">
            <div className="flex flex-col gap-1.5">
                <h2 className="m-0 text-[28px] font-extrabold tracking-[-0.03em]">Activa tu cuenta</h2>
                <p className="m-0 text-[15px] text-k-muted">
                    {model.emailFromLink
                        ? <>Enviamos un código a <strong className="text-k-ink">{model.email}</strong>. Escríbelo y crea tu contraseña.</>
                        : 'Escribe tu correo, el código que te enviamos y crea tu contraseña.'}
                </p>
            </div>
            {model.submitError && (
                <div role="alert" className="flex gap-2.5 rounded-xl bg-k-danger-soft px-3.5 py-3 text-sm font-semibold text-k-danger">
                    <AlertIcon className="shrink-0" />
                    {model.submitError}
                </div>
            )}
            <form className="flex flex-col gap-4" onSubmit={model.submit} noValidate>
                {!model.emailFromLink && (
                    <label className="flex flex-col gap-1.5 text-sm font-bold">
                        Correo electrónico
                        <input
                            type="email"
                            autoComplete="username"
                            value={model.email}
                            aria-invalid={model.invalid.email}
                            placeholder="nombre@correo.com"
                            onChange={(event) => model.setEmail(event.target.value)}
                            className={authFieldClass(model.invalid.email)}
                        />
                        {model.invalid.email && <span className="text-[13px] font-medium text-k-danger">Escribe un correo válido.</span>}
                    </label>
                )}
                <label className="flex flex-col gap-1.5 text-sm font-bold">
                    Código de activación
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={model.codigo}
                        maxLength={CODE_LENGTH}
                        aria-invalid={model.invalid.codigo}
                        placeholder="000000"
                        onChange={(event) => model.setCodigo(event.target.value)}
                        className={`${authFieldClass(model.invalid.codigo)} text-center text-xl font-bold tracking-[0.4em] tabular-nums`}
                    />
                    {model.invalid.codigo && <span className="text-[13px] font-medium text-k-danger">El código tiene {CODE_LENGTH} dígitos.</span>}
                </label>
                <div className="flex flex-col gap-1.5">
                    <label htmlFor="k-new-pass" className="text-sm font-bold">Contraseña</label>
                    {passwordField({ id: 'k-new-pass', value: model.password, invalid: model.invalid.password, autoComplete: 'new-password', onChange: model.setPassword })}
                    {model.invalid.password
                        ? <span className="text-[13px] font-medium text-k-danger">Usa al menos {model.passwordMin} caracteres.</span>
                        : <PasswordStrength password={model.password} />}
                </div>
                <div className="flex flex-col gap-1.5">
                    <label htmlFor="k-repeat-pass" className="text-sm font-bold">Repite la contraseña</label>
                    {passwordField({ id: 'k-repeat-pass', value: model.repeated, invalid: model.invalid.repeated, autoComplete: 'new-password', onChange: model.setRepeated })}
                    {model.invalid.repeated && <span className="text-[13px] font-medium text-k-danger">Las contraseñas deben coincidir.</span>}
                </div>
                <button
                    type="submit"
                    disabled={model.loading}
                    className="flex h-[52px] items-center justify-center gap-2.5 rounded-[14px] border-0 text-base font-extrabold text-white"
                    style={{ background: AUTH_ACCENT, opacity: model.loading ? 0.85 : 1, cursor: model.loading ? 'progress' : 'pointer' }}
                >
                    {model.loading && <span aria-hidden="true" className="h-[18px] w-[18px] rounded-full border-2 border-white/40 border-t-white" style={{ animation: 'k-spin 0.8s linear infinite' }} />}
                    {model.loading ? 'Activando…' : 'Activar cuenta'}
                </button>
            </form>
            <p className="m-0 border-t border-k-line pt-[18px] text-center text-sm text-k-muted">
                ¿Ya la activaste? <Link to={LOGIN_PATH} className="font-bold text-k-accent-ink no-underline">Iniciar sesión</Link>
                {' · '}
                <Link to={REGISTER_PATH} className="font-bold text-k-accent-ink no-underline">Registrarme de nuevo</Link>
            </p>
        </AuthLayout>
    )
}

export default RegisterActivate
